import { useRef } from "react";
import { toast } from "sonner";
import { Upload, X, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { api } from "../lib/api";
import { BRANDS, brandTheme } from "./RiderCard";

export const BrandChips = ({ value, onChange, cfg, testid = "brand" }) => (
  <div className="flex flex-wrap gap-2">
    {Object.keys(BRANDS).map((k) => { const t = brandTheme(cfg, k); const on = value === k; return (
      <button key={k} type="button" data-testid={`${testid}-${k}`} onClick={() => onChange(k)}
        className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${on ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
        <span className="flex h-4 w-6 overflow-hidden rounded-sm ring-1 ring-black/10"><span className="flex-1" style={{ background: t.primary }} /><span className="flex-1" style={{ background: t.secondary }} /></span>
        {k === "custom" ? (t.text && t.text !== BRANDS.custom.text ? t.text : t.label) : t.label}
      </button>); })}
  </div>
);

const COLORS = [["primary", "اللون الأساسي"], ["secondary", "اللون الثانوي"], ["onPrimary", "لون النص على الأساسي"], ["onSecondary", "لون النص على الثانوي"]];

export const CardBrandEditor = ({ f, setF }) => {
  const k = brandTheme(f, f.brand).key;
  const t = brandTheme(f, k);
  const inp = useRef(null);
  const setB = (patch) => setF((p) => ({ ...p, brands: { ...(p.brands || {}), [k]: { ...((p.brands || {})[k] || {}), ...patch } } }));
  const reset = () => setF((p) => { const b = { ...(p.brands || {}) }; delete b[k]; return { ...p, brands: b }; });
  const useMine = async () => {
    try {
      const d = await api.riderCardIdentity();
      if (!d.logo_id && !d.primary) { toast.error("ارفع شعار شركتك أولاً من «إعدادات الشركة»"); return; }
      const b = Object.fromEntries(["primary", "secondary", "onPrimary", "onSecondary", "logo_id", "text"].filter((x) => d[x]).map((x) => [x, d[x]]));
      setF((p) => ({ ...p, brand: "custom", logo_id: p.logo_id || d.logo_id, company_name: p.company_name || d.name, brands: { ...(p.brands || {}), custom: { ...((p.brands || {}).custom || {}), ...b } } }));
      toast.success(d.primary ? "طُبّقت هوية شركتك — احفظ الإعدادات" : "طُبّق الشعار — لم تُستخرج ألوان، اخترها يدوياً");
    } catch { toast.error("تعذّر جلب هوية الشركة"); }
  };
  const up = async (x) => { if (!x) return; try { const r = await api.uploadFile(x); setB({ logo_id: r.attachment_id }); } catch { toast.error("فشل الرفع"); } };
  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-3 sm:col-span-2" data-testid="cfg-brand-editor">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-bold text-slate-600">تطبيق التوصيل (التصميم الافتراضي للبطاقات الجديدة)</p>
        <Button size="sm" type="button" onClick={useMine} className="gap-1.5 bg-indigo-600 hover:bg-indigo-700" data-testid="cfg-use-company-identity"><Sparkles className="h-4 w-4" /> استخدام هوية شركتي</Button>
      </div>
      <BrandChips value={k} onChange={(v) => setF((p) => ({ ...p, brand: v }))} cfg={f} testid="cfg-brand" />
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="space-y-1 text-xs font-semibold text-slate-600 sm:col-span-2">اسم التطبيق على البطاقة (سطر جديد = Enter)
          <textarea data-testid="cfg-brand-text" dir="ltr" rows={2} value={t.text || ""} onChange={(e) => setB({ text: e.target.value })} className="w-full rounded-md border border-input px-3 py-1.5 text-sm font-normal" />
        </label>
        {COLORS.map(([c, l]) => (
          <label key={c} className="flex items-center gap-2 text-xs font-semibold text-slate-600">
            <input type="color" data-testid={`cfg-brand-${c}`} value={t[c]} onChange={(e) => setB({ [c]: e.target.value })} className="h-8 w-10 cursor-pointer rounded border border-slate-200" />
            {l} <Input dir="ltr" value={t[c]} onChange={(e) => setB({ [c]: e.target.value })} className="h-8 w-24 text-xs" />
          </label>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <input ref={inp} type="file" accept="image/*" hidden onChange={(e) => { up(e.target.files?.[0]); e.target.value = ""; }} />
        <Button size="sm" variant="outline" data-testid="cfg-brand-logo" onClick={() => inp.current?.click()} className="gap-1.5"><Upload className="h-4 w-4" /> {t.logo_id ? "تغيير شعار التطبيق" : "رفع شعار التطبيق (اختياري)"}</Button>
        {t.logo_id && <Button size="sm" variant="outline" data-testid="cfg-brand-logo-remove" onClick={() => setB({ logo_id: null })} className="text-red-600"><X className="h-4 w-4" /></Button>}
        <Button size="sm" variant="ghost" data-testid="cfg-brand-reset" onClick={reset}>استعادة الألوان الأصلية</Button>
      </div>
    </div>
  );
};
